// THE ONE re-hydrate seam for the ACCOUNT identity writes (contract 22 §5/§8) — the account name,
// the account avatar, the MFA flag, the user's name and `has_password`. Every Account/Profile flow
// ends here, so "which 07 entry point do these mutations use?" is a one-line change in this file
// rather than a grep across five flows.
//
// ⚠ SIBLING: `refreshWorkspaceRecord()` (ORP-18885) is the same 07 call under the other domain
// name — it re-reads `account_core.workspaces` for `label`/`timezone`. Both exist because a Basics
// flow reading `reloadAccountContext()` and an account-rename flow reading
// `refreshWorkspaceRecord()` would each be misleading at their call sites. The BEHAVIOUR is kept
// deliberately identical (including "skipped is unconfirmed, not success"); if the two are ever
// collapsed it should be into one neutrally-named seam, not by pointing one domain at the other.
//
// v3 had two answers and used them interchangeably. `AccountBootstrapFlow.execute(true)` — the one
// the Account/Profile panes used (`AccountSettings.vue:120`, `Mfa.vue:113`,
// `ChangeImageSection.vue:203`, `ProfileSettings.vue:89`) — is a whole re-boot: it moves
// `session.status` to `'booting'`, so the shell paints the full-page splash over the save and a
// failed reload replaces the app with `BootErrorState`. `loadSelectedAccountContext(force)` is the
// narrow reload: it re-reads `session_access` (the renamed user, the refreshed `has_password`) and
// `account_detail` (the avatar, the MFA flag), then recomputes permissions/packageId — the half
// v3's granular path was missing — and `session.status` never moves.
//
// `force: true` is required, not defensive: without it the flow no-ops (`status:'skipped'`) while
// `accountDetail` is warm, which it always is here, and only `force` busts the server cache
// (BE-07-09).
import { useAccount } from '@/features/account'

/**
 * Returns `false` when the re-read is UNCONFIRMED — failed, or skipped for want of an account.
 * The write itself already landed, so the caller reports success plus a non-blocking
 * `workspaceSettings.common.refreshFailed` notice rather than an error (§14).
 */
export async function reloadAccountContext(): Promise<boolean> {
  const outcome = await useAccount().loadSelectedAccountContext(true)
  return outcome.status === 'reloaded'
}
